import { useState, useEffect, useRef } from 'react';
import { apiGet } from '../utils/api';
import { useDisguise } from '../context/DisguiseContext';

// ── 기능 플래그 ────────────────────────────────────────
// 패턴 표시 여부.
//   2026-08-05 운영 공개로 전환. 그 전에는 /dev 빌드에서만 노출했다.
//   되돌리려면 빌드 시 REACT_APP_PATTERN=off 를 주면 된다.
//     REACT_APP_PATTERN=off npm run build   → 패턴 숨김
//   추가 안전장치: 플래그가 true 여도 /api/pattern/views 가 비면 자동 숨김이므로
//   배치 미실행·서버 미배포 상태에서 빈 메뉴가 뜨는 일은 없다.
const SHOW_PATTERN = (process.env.REACT_APP_PATTERN || '').toLowerCase() !== 'off';

// ── 차트 형상(일봉 30봉) 보조 정의 ────────────────────────
//   과거 봉의 모양을 분류한 결과. 향후 방향을 예측하지 않음.
const PATTERN_EASY = {
  // 상승 마무리
  rise_continue:  '최근 30거래일 처음부터 꾸준히 오른 흐름이었던 종목입니다.',
  rise_after_dip: '오르다가 중간에 한 번 눌린 뒤, 마지막 구간에서 다시 오른 종목입니다.',
  rise_after_flat:'한동안 큰 방향 없이 움직이다가 마지막 구간에서 오른 종목입니다.',
  v_bottom:       '가운데 부근까지 깊이 빠졌다가 대칭으로 반등해 회복한 종목입니다.',
  double_bottom:  '두 번 바닥을 다지고 사이를 반등한 뒤 마지막에 위로 오른 종목입니다.',
  // 관망
  rebound_try:    '전반적으로 내렸으나 마지막 구간에서 반등을 시도한 종목입니다. (아직 시작보다는 낮음)',
  sideways:       '최근 30거래일 동안 시작과 끝이 거의 같았던 종목입니다. 뚜렷한 방향이 없었습니다.',
  // 하락 마무리
  fall_from_top:  '초·중반 오르다 고점을 찍고 마지막 구간에서 내린 종목입니다.',
  fall_after_pop: '내리던 중 한 번 반등했다가 마지막 구간에서 다시 내린 종목입니다.',
  fall_after_flat:'한동안 큰 방향 없이 움직이다가 마지막 구간에서 내린 종목입니다.',
  fall_continue:  '최근 30거래일 내림 흐름이 이어진 종목입니다.',
  // 급등락
  surge_up:       '최근 30거래일 고저 폭이 매우 컸고, 기간 전체로는 오른 종목입니다. 이미 크게 움직였습니다.',
  surge_down:     '최근 30거래일 고저 폭이 매우 컸고, 기간 전체로는 내린 종목입니다. 이미 크게 움직였습니다.',
};

// 스파크라인 — points 는 0~100 정규화 배열
function Spark({ points, color, width = 72, height = 26 }) {
  if (!points || points.length < 2) {
    return <span style={{ color: '#555' }}>-</span>;
  }
  const nn = points.length, pad = 2;
  const w = width - pad * 2, h = height - pad * 2;
  const d = points.map((v, i) =>
    `${pad + (i / (nn - 1)) * w},${pad + (1 - v / 100) * h}`).join(' ');
  return (
    <svg width={width} height={height} style={{ display: 'block', margin: '0 auto' }}>
      <polyline points={d} fill="none" stroke={color} strokeWidth="1.4"
        strokeLinejoin="round" strokeLinecap="round" />
    </svg>
  );
}

// ── 수급 스크리너(KRX 일별 데이터) 보조 정의 ──────────────
//   쉬운관점: 조건의 객관적 의미만 풀어씀 (예측·추천 표현 금지)
//   ★ 화면에는 이 문구와 서버 flow_service.py VIEWS[].desc 가 나란히 뜬다.
//     기준값을 바꾸면 양쪽을 함께 고칠 것 — 한쪽만 고치면 바로 어긋나 보인다.
//     (v4에서 연속순매수 하한이 '1억 고정'→'시총 구간별'로 바뀌었는데
//      문구를 못 따라가 "하루 1억원"이 오래 남아 있었음. 2026-08-01 정정)
const FLOW_EASY = {
  foreign_streak: '외국인이 시총 규모에 맞는 금액 이상 3일 넘게 연속으로 사들인 종목입니다.',
  inst_streak:    '기관이 시총 규모에 맞는 금액 이상 3일 넘게 연속으로 사들인 종목입니다.',
  foreign_focus:  '그날 거래대금 중 외국인 순매수 비중이 큰 종목입니다. (200억 이상)',
  inst_focus:     '그날 거래대금 중 기관 순매수 비중이 큰 종목입니다. (200억 이상)',
  surge_up:       '평소(최근 20일 중앙값)보다 거래량이 2배 이상 늘면서 오른 채로 마감한 종목입니다.',
  surge_down:     '평소(최근 20일 중앙값)보다 거래량이 2배 이상 늘면서 내린 채로 마감한 종목입니다.',
};

// 뷰별 대표 수치 (5분대비 컬럼 자리에 표시)
// ── 장중 거래량 배율 ────────────────────────────────────
//   배율 = 현재 누적거래량 ÷ 최근 20영업일 거래량 중앙값
//   중앙값은 /api/flow/baseline 로 1회만 받고, 현재 거래량은
//   기존 WebSocket 틱에 이미 들어온다 -> 서버 추가 호출 0
//
//   주의: 장 초반에는 누적 거래량이 적어 배율이 낮게 나온다.
//        원값을 그대로 보여주고, 경과율 기준 추정치는 툴팁에만 둔다.
//        (실제 거래는 장 초반·마감 직전에 몰려 시간에 비례하지 않으므로
//         보정치를 전면에 내세우면 오전에 과대평가된다.)
const MKT_OPEN_MIN  = 9 * 60;        // 09:00
const MKT_CLOSE_MIN = 15 * 60 + 30;  // 15:30

function marketElapsed() {
  const now = new Date();
  const cur = now.getHours() * 60 + now.getMinutes();
  if (now.getDay() === 0 || now.getDay() === 6) return null;   // 주말
  if (cur < MKT_OPEN_MIN || cur > MKT_CLOSE_MIN) return null;  // 장외
  return (cur - MKT_OPEN_MIN) / (MKT_CLOSE_MIN - MKT_OPEN_MIN);
}

function fmtRatio(r) {
  if (r === null || r === undefined || !isFinite(r)) return null;
  return r >= 10 ? `${Math.round(r)}배` : `${r.toFixed(1)}배`;
}

// 지표 컬럼 윗줄 라벨. 아랫줄은 '당일'(장중 실시간 거래량 배수) 고정.
//   surge_* 를 '배율'로 두면 아랫줄과 단어가 겹쳐 시점 구분이 안 된다 -> '거래량'.
const FLOW_COL_LABEL = {
  foreign_streak: '연속',     inst_streak: '연속',
  foreign_focus: '대금비중',  inst_focus: '대금비중',
  surge_up: '거래량',         surge_down: '거래량',
};

// 모바일 등락률 칸 아랫줄용 축약 라벨 (폭이 좁아 '대금비중'은 넘친다)
const FLOW_COL_LABEL_SHORT = {
  foreign_streak: '연속',  inst_streak: '연속',
  foreign_focus: '비중',   inst_focus: '비중',
  surge_up: '거래',        surge_down: '거래',
};

function flowMetric(key, it) {
  // 수급집중(비중)·거래량급증(배수)은 값 자체에 방향성이 없다.
  // 색을 넣으면 옆의 등락률 색과 충돌하고, 상승/하락은 이미 조건명에 있으므로 중립.
  const pct = v => (v == null ? '-' : `${Math.round(v)}%`);
  const mul = v => (v == null ? '-' : `${v}배`);
  if (key === 'foreign_streak') return { text: `${it.foreign_streak}일`, tone: 'up' };
  if (key === 'inst_streak')    return { text: `${it.inst_streak}일`,    tone: 'up' };
  if (key === 'foreign_focus')  return { text: pct(it.foreign_intensity), tone: 'flat' };
  if (key === 'inst_focus')     return { text: pct(it.inst_intensity),    tone: 'flat' };
  if (key === 'surge_up')       return { text: mul(it.volume_ratio), tone: 'flat' };
  if (key === 'surge_down')     return { text: mul(it.volume_ratio), tone: 'flat' };
  return { text: '-', tone: 'flat' };
}


// 컬럼 폭 고정 — 탭/정렬/위장 모드 무관 절대 변동 없음
const COL = {
  name:        140,
  price:        82,
  changeRate:   88,   // 패턴 모드에서 당일% + 30일% 2줄 표기
  tradeValue:   78,
  change5m:     76,
};

// 위장 모드 라벨 매핑 (영어 데이터 분석 보고서 스킨)
const L = {
  normal: {
    title:   '실시간 시세',
    loading: '로딩 중...',
    empty:   '데이터 없음',
    market:  { all: '전체', KOSPI: '코스피', KOSDAQ: '코스닥' },
    sort:    { RISE: '📈 상승률', FALL: '📉 하락률', TRADE_VALUE: '💰 거래금액' },
    col:     { name: '종목명', price: '현재가', changeRate: '등락률', tradeValue: '거래금액', change5m: '5분대비' },
  },
  disguised: {
    title:   '📊 Q3 Data Analysis',
    loading: 'Loading...',
    empty:   'No data',
    market:  { all: 'All', KOSPI: 'Group A', KOSDAQ: 'Group B' },
    sort:    { RISE: 'Sort: Asc', FALL: 'Sort: Desc', TRADE_VALUE: 'Sort: Weight' },
    col:     { name: 'Item', price: 'Score', changeRate: 'Variance', tradeValue: 'Volume', change5m: 'Δ (5m)' },
  },
};

// 위장 시 종목명/코드를 추상화
function disguiseName(name, idx) {
  // 알파벳 A-Z 26개 + 숫자 조합으로 50개 커버
  const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
  if (idx < 26) return `Item ${letters[idx]}`;
  return `Item ${letters[Math.floor(idx / 26) - 1]}${idx % 26}`;
}
function disguiseCode(code, idx) {
  return `ID-${String(idx + 1).padStart(3, '0')}`;
}

// 위장 시 색상 (회색조)
const DISGUISE_COLORS = {
  up:      '#9ca3af',   // 옅은 회색 (원래 빨강)
  down:    '#6b7280',   // 진한 회색 (원래 파랑)
  neutral: '#4b5563',
};
const NORMAL_COLORS = {
  up:      '#f85149',   // 빨강
  down:    '#3b82f6',   // 파랑
  neutral: '#8b949e',
};

// 조건검색 쉬운관점 (초보자용 풀이) — key는 백엔드 SCREENS와 동일
const SCREENER_EASY = {
  gap:    '시작부터 어제 가격은 주지도 않을 만큼 강하게 시가가 떴다. (강력한 수급 출발)',
  igyuk:  '한 달 평균 가격보다 20% 이상 주가가 붕 떴다. (가격적 과열)',
  psych:  '10일 중 8일 이상 종가를 양봉으로 마감했다. (지속적인 관리 수급)',
  rsi:    '3주간의 상승 에너지가 폭발하여 기술적 과열의 정점에 달했다. (모멘텀의 정점)',
  golden: '중기 추세가 이제 막 하락에서 상승으로 턴했다. (방향성 전환)',
};


// 글자 크기 단계 (틀 깨지지 않는 안전 범위)
const FONT_SIZE = {
  S: { name: 11, price: 11, rate: 11, value: 11, change5m: 10, code: 10, th: 11 },
  M: { name: 13, price: 13, rate: 13, value: 12, change5m: 11, code: 11, th: 12 },
  L: { name: 14, price: 14, rate: 14, value: 13, change5m: 12, code: 12, th: 13 },
};

export default function StockTable({ onSelect, selected, tickData, onStocksLoaded }) {
  const { disguised, excel } = useDisguise();
  const labels  = disguised ? L.disguised : L.normal;
  const colors  = disguised ? DISGUISE_COLORS : NORMAL_COLORS;

  // 글자 크기 (localStorage 저장)
  const [sizeKey, setSizeKey] = useState(
    () => localStorage.getItem('stocktable_fontsize') || 'M'
  );
  const fs = FONT_SIZE[sizeKey];
  function cycleSize(next) {
    setSizeKey(next);
    localStorage.setItem('stocktable_fontsize', next);
  }

  const [stocks, setStocks]       = useState([]);
  const [loading, setLoading]     = useState(false);
  const [market, setMarket]       = useState('all');
  const [sortType, setSortType]   = useState('RISE');
  const [innerSort, setInnerSort] = useState('change_rate');
  const [sortDir, setSortDir]     = useState('desc');
  const [includeEtf, setIncludeEtf] = useState(false);
  const [screeners, setScreeners]     = useState([]);   // [{key,label,desc}]
  const [screenerKey, setScreenerKey] = useState(null); // null=시세 모드, 값=조건검색
  const [flows, setFlows]             = useState([]);   // 수급 뷰 [{key,label,desc}]
  const [flowCnt, setFlowCnt]         = useState({});   // 수급 뷰별 종목 수
  // 면책 펼침 여부. 모바일은 설명+면책이 화면의 절반을 먹어 종목이 2~3개밖에
  // 안 보이므로 기본 접힘. PC는 공간이 있어 그대로 펼침.
  const [discOpen, setDiscOpen]       = useState(false);
  const [flowKey, setFlowKey]         = useState(null); // null=미사용, 값=수급 스크리너
  const [flowBase, setFlowBase]       = useState(null); // 수급 데이터 기준일
  const [baseline, setBaseline]       = useState({});   // code -> 20일 거래량 중앙값
  const [patterns, setPatterns]       = useState([]);   // 형상 목록
  const [patternKey, setPatternKey]   = useState(null); // null=미사용
  const [patternBase, setPatternBase] = useState(null); // 형상 기준일
  const [patternCnt, setPatternCnt]   = useState({});   // 형상별 종목 수
  const [isMobile, setIsMobile]   = useState(
    typeof window !== 'undefined' && window.innerWidth < 600
  );

  useEffect(() => {
    function onResize() { setIsMobile(window.innerWidth < 600); }
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // 조건검색 메뉴(라벨+설명) 1회 로드
  useEffect(() => {
    apiGet('/api/screener')
      .then(d => setScreeners(Array.isArray(d) ? d : []))
      .catch(() => {});
  }, []);

  // 수급 스크리너 뷰 목록 + 건수 1회 로드 (배치 미구축 서버에서도 조용히 실패)
  useEffect(() => {
    apiGet('/api/flow/views')
      .then(d => setFlows(Array.isArray(d) ? d : []))
      .catch(() => {});
    apiGet('/api/flow/summary')
      .then(d => setFlowCnt(d?.counts || {}))
      .catch(() => {});
  }, []);

  // 장중 배율 기준값(20영업일 거래량 중앙값) 1회 로드 — 약 2KB
  useEffect(() => {
    apiGet('/api/flow/baseline')
      .then(d => setBaseline(d?.median || {}))
      .catch(() => {});
  }, []);

  // 차트 형상 목록 + 건수 1회 로드 (플래그 off 거나 배치 미실행이면 빈 채로)
  useEffect(() => {
    if (!SHOW_PATTERN) return;
    apiGet('/api/pattern/views')
      .then(d => setPatterns(Array.isArray(d) ? d : []))
      .catch(() => {});
    apiGet('/api/pattern/summary')
      .then(d => setPatternCnt(d?.counts || {}))
      .catch(() => {});
  }, []);

  useEffect(() => {
    fetchStocks();
    const interval = setInterval(fetchStocks, 1 * 60 * 1000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [market, sortType, includeEtf, screenerKey, flowKey, patternKey]);

  // 상단 정렬 탭과 테이블 내부 정렬 동기화 (하락률 = asc)
  useEffect(() => {
    if (sortType === 'TRADE_VALUE') {
      setInnerSort('trade_value');
      setSortDir('desc');
    } else if (sortType === 'FALL') {
      setInnerSort('change_rate');
      setSortDir('asc');
    } else {
      setInnerSort('change_rate');
      setSortDir('desc');
    }
  }, [sortType]);

  // 컬럼 폭 - 모바일에서는 종목명(유동) + 등락률(고정) 2컬럼
  //   등락률 칸은 flow/pattern 모드에서 지표를 아랫줄에 병기하므로 여유를 둔다.
  const col = isMobile
    ? { name: 'auto', price: 0, changeRate: 78, tradeValue: 0, change5m: 0 }
    : COL;
  const showPrice      = !isMobile;   // 현재가 컬럼 (모바일 숨김)
  const showTradeValue = !isMobile;
  const showChange5m   = !isMobile;

  useEffect(() => {
    if (!tickData) return;
    setStocks(prev => prev.map(s =>
      s.code === tickData.code
        ? {
            ...s,
            price: tickData.price,
            change_rate: tickData.change_rate,
            // 장중 배율 계산용 (틱에 누적거래량이 들어옴)
            live_volume: tickData.volume ?? s.live_volume,
          }
        : s
    ));
  }, [tickData]);

  // onStocksLoaded debounce (500ms)
  // 탭 연속 변경 시 마지막 호출만 실행 → WebSocket 업데이트 폭증 방지
  const debounceRef = useRef(null);
  function notifyStocksLoaded(codes) {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      if (onStocksLoaded) onStocksLoaded(codes);
    }, 500);
  }

  async function fetchStocks() {
    setLoading(true);
    try {
      let data = [];
      if (patternKey) {
        // 차트 형상 — 일봉 30봉 (전일 확정)
        const d = await apiGet(`/api/pattern/view/${patternKey}?top_n=30`);
        const items = Array.isArray(d?.items) ? d.items : [];
        setPatternBase(d?.base_date_fmt || null);
        data = items.map(it => ({
          code: it.code, name: it.name, price: it.close,
          change_rate: it.change_pct, trade_value: it.value,
          spark: it.spark, period_pct: it.period_pct,
        }));
      } else if (flowKey) {
        // 수급 스크리너 — KRX 일별 데이터 (전일 확정치, 하루 1회 배치)
        const d = await apiGet(`/api/flow/view/${flowKey}?top_n=30`);
        const items = Array.isArray(d?.items) ? d.items : [];
        setFlowBase(d?.base_date_fmt || null);
        // 기존 테이블 필드명에 맞춰 매핑 (실시간 tick 갱신도 그대로 적용됨)
        data = items.map(it => ({
          code:        it.code,
          name:        it.name,
          price:       it.close,
          change_rate: it.change_pct,
          trade_value: it.value,
          flow_metric: flowMetric(flowKey, it),
        }));
      } else if (screenerKey) {
        // 조건검색 모드 — 네이버 시세 프리셋 (정렬 없이 네이버 순서 유지)
        const d = await apiGet(`/api/screener/${screenerKey}?top_n=30`);
        data = Array.isArray(d?.items) ? d.items : [];
      } else {
        const etfQuery = includeEtf ? '&include_etf=true' : '';
        const url = sortType === 'TRADE_VALUE'
          ? `/api/stock/trade-value?market=${market}&top_n=30${etfQuery}`
          : `/api/stock/top-movers?market=${market}&change_type=${sortType}&top_n=30${etfQuery}`;
        const d = await apiGet(url);
        data = Array.isArray(d) ? d : [];
      }
      setStocks(data);
      if (onStocksLoaded) {
        notifyStocksLoaded(data.slice(0, 30).map(s => s.code).filter(Boolean));
      }
    } catch (e) {
      console.error('시세 오류:', e);
    }
    setLoading(false);
  }

  function handleHeaderClick(key) {
    if (innerSort === key) {
      setSortDir(prev => (prev === 'desc' ? 'asc' : 'desc'));
    } else {
      setInnerSort(key);
      if (key === 'change_rate' && sortType === 'FALL') {
        setSortDir('asc');
      } else {
        setSortDir('desc');
      }
    }
  }

  const sortedMovers = [...stocks].sort((a, b) => {
    let result = 0;
    if (innerSort === 'change_rate') {
      result = (b.change_rate || 0) - (a.change_rate || 0);
    } else if (innerSort === 'trade_value') {
      result = (b.trade_value || 0) - (a.trade_value || 0);
    } else if (innerSort === 'volume') {
      result = (b.volume || 0) - (a.volume || 0);
    }
    return sortDir === 'desc' ? result : -result;
  });
  // 조건검색은 네이버가 준 순서 그대로 (정렬하지 않음)
  const sorted = (screenerKey || flowKey || patternKey) ? stocks : sortedMovers;
  const activeScreener = screeners.find(s => s.key === screenerKey);
  const activeFlow     = flows.find(f => f.key === flowKey);
  const activePattern  = patterns.find(p => p.key === patternKey);

  // 화살표: 사용자 눈에 보이는 방향 기준
  // 하락률 탭 등락률 컬럼은 asc = "가장 많이 하락한 것부터" = 사용자 눈엔 ▼
  const arrow = (key) => {
    if (innerSort !== key) return '';
    const isFallCol = sortType === 'FALL' && key === 'change_rate';
    // isFallCol이면 asc↔desc 의미가 반전됨
    const visualDesc = isFallCol ? (sortDir === 'asc') : (sortDir === 'desc');
    return visualDesc ? ' ▼' : ' ▲';
  };

  // 위장 모드 시 등락률/거래금액에 추가 음영 (사용자가 흘낏 봐도 무엇인지 파악 어렵게)
  const valueOpacity = disguised ? 0.55 : 1;

  return (
    <div style={styles.panel}>
      {/* 헤더 */}
      <div style={styles.header}>
        <span style={{ ...styles.label, display: 'inline-flex', alignItems: 'center', gap: 6 }}>
          {!disguised && (
            <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="#388bfd" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
              <line x1="7" y1="4" x2="7" y2="20" />
              <rect x="4.5" y="8" width="5" height="7" rx="1" fill="#388bfd" stroke="none" />
              <line x1="16" y1="4" x2="16" y2="20" />
              <rect x="13.5" y="6" width="5" height="9" rx="1" />
            </svg>
          )}
          {labels.title}
        </span>
        {loading && <span style={{ fontSize: 11, color: '#8b949e' }}>{labels.loading}</span>}
        <label style={{
          display: 'flex', alignItems: 'center', gap: 5,
          marginLeft: 'auto', fontSize: 14,
          color: excel ? '#333' : '#8b949e',
          cursor: 'pointer', userSelect: 'none',
        }}>
          <input
            type="checkbox"
            checked={includeEtf}
            onChange={(e) => setIncludeEtf(e.target.checked)}
            style={{ margin: 0, cursor: 'pointer', width: 16, height: 16 }}
          />
          <span>ETF</span>
        </label>
        <span style={{ fontSize: 14, color: '#8b949e', marginLeft: 12 }}>
          {(disguised || isMobile) ? '' : '금액(백만원)'}
        </span>
        <div style={{ display: 'flex', gap: 3, marginLeft: 12 }}>
          {['S','M','L'].map(k => (
            <button key={k} onClick={() => cycleSize(k)} style={{
              fontSize: 14, padding: '2px 9px', borderRadius: 4, cursor: 'pointer',
              border: '1px solid #30363d',
              background: sizeKey === k ? '#388bfd' : '#21262d',
              color: sizeKey === k ? '#fff' : '#8b949e',
            }}>{k}</button>
          ))}
        </div>
      </div>

      {/* 시장 필터 + 정렬 기준 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8, gap: 4, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 4, alignItems: 'center', flexWrap: 'wrap' }}>
          {[
            { key: 'all',    label: labels.market.all },
            { key: 'KOSPI',  label: labels.market.KOSPI },
            { key: 'KOSDAQ', label: labels.market.KOSDAQ },
          ].map(m => (
            <button
              key={m.key}
              onClick={() => { setScreenerKey(null); setFlowKey(null); setPatternKey(null); setMarket(m.key); }}
              style={{
                ...styles.filterBtn,
                background: (market === m.key && !screenerKey && !flowKey && !patternKey)
                  ? (excel ? '#217346' : '#388bfd')
                  : (excel ? '#fff' : '#21262d'),
                color: (market === m.key && !screenerKey && !flowKey && !patternKey) ? '#fff' : (excel ? '#333' : '#8b949e'),
                border: excel ? '1px solid #d0d0d0' : 'none',
              }}
            >
              {m.label}
            </button>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 4 }}>
          {[
            { key: 'RISE',        label: labels.sort.RISE },
            { key: 'FALL',        label: labels.sort.FALL },
            { key: 'TRADE_VALUE', label: labels.sort.TRADE_VALUE },
          ].map(s => (
            <button
              key={s.key}
              onClick={() => { setScreenerKey(null); setFlowKey(null); setPatternKey(null); setSortType(s.key); }}
              style={{
                ...styles.filterBtn,
                background: (sortType === s.key && !screenerKey && !flowKey && !patternKey) ? '#f0883e' : '#21262d',
                color:      (sortType === s.key && !screenerKey && !flowKey && !patternKey) ? '#fff' : '#8b949e',
              }}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* 조건검색 드롭다운 + 쉬운관점/면책. 위장 모드에선 숨김 */}
      {!disguised && !excel && (screeners.length > 0 || flows.length > 0) && (
        <div style={{ marginBottom: 8 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <select
              value={screenerKey || ''}
              onChange={(e) => {
                const v = e.target.value || null;
                setScreenerKey(v);
                if (v) { setFlowKey(null); setPatternKey(null); }  // 다른 모드 해제 (안내 박스 잔존 방지)
              }}
              style={{
                fontSize: 12, padding: '3px 6px', borderRadius: 4, cursor: 'pointer',
                flexShrink: 0,
                background: screenerKey ? '#388bfd' : '#21262d',
                color:      screenerKey ? '#fff'    : '#c9d1d9',
                border: '1px solid #30363d',
              }}
            >
              <option value="">🔍 조건검색</option>
              {screeners.map(s => (
                <option key={s.key} value={s.key}>{s.label}</option>
              ))}
            </select>

            {/* 수급 스크리너 (KRX 일별 데이터) */}
            {flows.length > 0 && (
              <select
                value={flowKey || ''}
                onChange={(e) => {
                  const v = e.target.value || null;
                  setFlowKey(v);
                  if (v) { setScreenerKey(null); setPatternKey(null); }
                }}
                style={{
                  fontSize: 12, padding: '3px 6px', borderRadius: 4, cursor: 'pointer',
                  flexShrink: 0,
                  background: flowKey ? '#238636' : '#21262d',
                  color:      flowKey ? '#fff'    : '#c9d1d9',
                  border: '1px solid #30363d',
                }}
              >
                <option value="">📈 확정수급 (기준일)</option>
                {flows.map(f => (
                  <option key={f.key} value={f.key}>
                    {f.label}{flowCnt[f.key] ? ` (${flowCnt[f.key]})` : ''}
                  </option>
                ))}
              </select>
            )}

            {/* 차트 형상 (일봉 30봉) — 플래그 on + 목록 존재 시에만 */}
            {SHOW_PATTERN && patterns.length > 0 && (
              <select
                value={patternKey || ''}
                onChange={(e) => {
                  const v = e.target.value || null;
                  setPatternKey(v);
                  if (v) { setScreenerKey(null); setFlowKey(null); }
                }}
                style={{
                  fontSize: 12, padding: '3px 6px', borderRadius: 4, cursor: 'pointer',
                  flexShrink: 0,
                  background: patternKey ? '#8957e5' : '#21262d',
                  color:      patternKey ? '#fff'    : '#c9d1d9',
                  border: '1px solid #30363d',
                }}
              >
                <option value="">📐 차트모양</option>
                {[
                  ['rise',  '상승형'],
                  ['watch', '관망'],
                  ['fall',  '하락형'],
                  ['surge', '급등락'],
                ].map(([dir, gl]) => {
                  const items = patterns.filter(p => p.direction === dir);
                  if (items.length === 0) return null;
                  return (
                    <optgroup key={dir} label={gl}>
                      {items.map(p => (
                        <option key={p.key} value={p.key}>
                          {p.label}{patternCnt[p.key] ? ` (${patternCnt[p.key]})` : ''}
                        </option>
                      ))}
                    </optgroup>
                  );
                })}
              </select>
            )}

            {flowKey && flowBase && (
              <span style={{ fontSize: 11, color: '#8b949e', whiteSpace: 'nowrap' }}>
                기준일 {flowBase}
              </span>
            )}
            {patternKey && patternBase && (
              <span style={{ fontSize: 11, color: '#8b949e', whiteSpace: 'nowrap' }}>
                기준일 {patternBase} · 30봉
              </span>
            )}
          </div>

          {patternKey && activePattern && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 12, color: '#c9d1d9', lineHeight: 1.5 }}>
                <span style={{ color: '#a371f7', fontWeight: 700 }}>쉬운관점</span>
                <span style={{ color: '#6e7681' }}>{'  ·  '}</span>
                {PATTERN_EASY[patternKey] || activePattern.desc}
              </div>
              {!isMobile && (
                <div style={{ fontSize: 11, color: '#6e7681', marginTop: 2, lineHeight: 1.4 }}>
                  {activePattern.desc}
                </div>
              )}
              {(isMobile && !discOpen) ? (
                <div
                  onClick={() => setDiscOpen(true)}
                  style={{
                    marginTop: 6, fontSize: 11, color: '#e3b341',
                    cursor: 'pointer', userSelect: 'none',
                  }}
                >
                  ⚠️ 예측 아님 · 형태 분류 안내 <span style={{ color: '#9e6a03' }}>▾</span>
                </div>
              ) : (
                <div style={{
                  marginTop: 7, padding: '7px 10px', borderRadius: 5,
                  border: '1px solid #9e6a03', background: 'rgba(187,128,9,0.12)',
                  fontSize: 11, color: '#e3b341', lineHeight: 1.55,
                }}>
                  <div>⚠️ 지난 30거래일 종가의 <b>모양을 분류한 결과</b>일 뿐이며, 앞으로의 주가 방향을 예측하거나 시사하지 않습니다.</div>
                  <div style={{ marginTop: 4 }}>※ 과거에 특정 모양이었다는 사실이 이후 같은 방향을 의미하지 않습니다. 투자 판단과 책임은 본인에게 있습니다.</div>
                  {isMobile && (
                    <div
                      onClick={() => setDiscOpen(false)}
                      style={{ marginTop: 4, color: '#9e6a03', cursor: 'pointer', userSelect: 'none' }}
                    >접기 ▴</div>
                  )}
                </div>
              )}
            </div>
          )}

          {flowKey && activeFlow && (
            <div style={{ marginTop: 6 }}>
              <div style={{ fontSize: 12, color: '#c9d1d9', lineHeight: 1.5 }}>
                <span style={{ color: '#3fb950', fontWeight: 700 }}>쉬운관점</span>
                <span style={{ color: '#6e7681' }}>{'  ·  '}</span>
                {FLOW_EASY[flowKey] || activeFlow.desc}
              </div>
              {/* desc 는 쉬운관점과 내용이 겹친다. 좁은 화면에선 생략. */}
              {!isMobile && (
                <div style={{ fontSize: 11, color: '#6e7681', marginTop: 2, lineHeight: 1.4 }}>
                  {activeFlow.desc}
                </div>
              )}
              {(isMobile && !discOpen) ? (
                <div
                  onClick={() => setDiscOpen(true)}
                  style={{
                    marginTop: 6, fontSize: 11, color: '#e3b341',
                    cursor: 'pointer', userSelect: 'none',
                  }}
                >
                  ⚠️ 투자 권유 아님 · 확정수급 기준 안내 <span style={{ color: '#9e6a03' }}>▾</span>
                </div>
              ) : (
                <div style={{
                  marginTop: 7, padding: '7px 10px', borderRadius: 5,
                  border: '1px solid #9e6a03', background: 'rgba(187,128,9,0.12)',
                  fontSize: 11, color: '#e3b341', lineHeight: 1.55,
                }}>
                  <div>⚠️ 종목 추천·투자 권유가 아니며, 향후 주가 방향을 예측하지 않습니다. 투자 판단과 책임은 본인에게 있습니다.</div>
                  <div style={{ marginTop: 3 }}>※ 한국거래소 <b>확정 수급</b> 기준(기준일 값). 현재가·등락률·당일 거래량만 실시간입니다.</div>
                  {isMobile && (
                    <div
                      onClick={() => setDiscOpen(false)}
                      style={{ marginTop: 4, color: '#9e6a03', cursor: 'pointer', userSelect: 'none' }}
                    >접기 ▴</div>
                  )}
                </div>
              )}
            </div>
          )}

          {screenerKey && activeScreener && (
            <div style={{ marginTop: 6 }}>
              {/* 쉬운관점 */}
              <div style={{ fontSize: 12, color: '#c9d1d9', lineHeight: 1.5 }}>
                <span style={{ color: '#58a6ff', fontWeight: 700 }}>쉬운관점</span>
                <span style={{ color: '#6e7681' }}>{'  ·  '}</span>
                {SCREENER_EASY[screenerKey] || activeScreener.desc}
              </div>
              {/* 기술 정의 — 쉬운관점과 겹치므로 좁은 화면에선 생략 */}
              {!isMobile && (
                <div style={{ fontSize: 11, color: '#6e7681', marginTop: 2, lineHeight: 1.4 }}>
                  {activeScreener.desc}
                </div>
              )}
              {/* 안내·면책 — 모바일은 기본 접힘 */}
              {(isMobile && !discOpen) ? (
                <div
                  onClick={() => setDiscOpen(true)}
                  style={{
                    marginTop: 6, fontSize: 11, color: '#e3b341',
                    cursor: 'pointer', userSelect: 'none',
                  }}
                >
                  ⚠️ 투자 권유 아님 · 조건검색 안내 <span style={{ color: '#9e6a03' }}>▾</span>
                </div>
              ) : (
                <div style={{
                  marginTop: 7, padding: '7px 10px', borderRadius: 5,
                  border: '1px solid #9e6a03', background: 'rgba(187,128,9,0.12)',
                  fontSize: 11, color: '#e3b341', lineHeight: 1.55,
                }}>
                  <div>⚠️ <b>운영자의 종목 추천·투자 권유가 아닙니다.</b> ‘쉬운관점’은 조건검색식의 객관적 의미를 풀어쓴 설명이며, 투자 판단과 책임은 이용자 본인에게 있습니다.</div>
                  <div style={{ marginTop: 4 }}>※ 조건검색 결과는 <b>스냅샷</b>이라 ‘5분대비’ 정보는 제공하지 않습니다 (<span style={{ color: '#8b949e' }}>-</span> 로 표시).</div>
                  {isMobile && (
                    <div
                      onClick={() => setDiscOpen(false)}
                      style={{ marginTop: 4, color: '#9e6a03', cursor: 'pointer', userSelect: 'none' }}
                    >접기 ▴</div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* 테이블 */}
      <div style={styles.tableWrap}>
        <table style={{ ...styles.table, tableLayout: 'fixed' }}>
          <colgroup>
            <col style={col.name === 'auto' ? {} : { width: col.name }} />
            {showPrice && <col style={{ width: col.price }} />}
            <col style={{ width: col.changeRate }} />
            {showTradeValue && <col style={{ width: col.tradeValue }} />}
            {showChange5m && <col style={{ width: col.change5m }} />}
          </colgroup>
          <thead>
            <tr style={styles.thead}>
              <th style={{ ...styles.th, ...(col.name === 'auto' ? {} : { width: col.name }), fontSize: fs.th }}>{labels.col.name}</th>
              {showPrice && (
                <th style={{ ...styles.th, width: col.price, textAlign: 'right', fontSize: fs.th }}>{labels.col.price}</th>
              )}
              <th
                style={{ ...styles.th, width: col.changeRate, textAlign: 'right', cursor: 'pointer', fontSize: fs.th }}
                onClick={() => handleHeaderClick('change_rate')}
                title={patternKey ? '윗줄: 당일 등락률 / 아랫줄: 30거래일 기간 수익률(형태 분류 기준)' : undefined}
              >
                {patternKey ? `당일 ${labels.col.changeRate}` : labels.col.changeRate}{arrow('change_rate')}
              </th>
              {showTradeValue && (
                <th
                  style={{ ...styles.th, width: col.tradeValue, textAlign: 'right', cursor: 'pointer', fontSize: fs.th }}
                  onClick={() => handleHeaderClick('trade_value')}
                >
                  {labels.col.tradeValue}{arrow('trade_value')}
                </th>
              )}
              {showChange5m && (
                <th style={{ ...styles.th, width: col.change5m, textAlign: 'center', fontSize: fs.th }}>
                  {patternKey ? '30일 흐름'
                    : flowKey ? (
                    <span style={{ display: 'inline-block', lineHeight: 1.15 }}>
                      <span>{FLOW_COL_LABEL[flowKey] || '지표'}</span><br />
                      <span style={{ fontSize: '0.82em', color: '#6e7681', fontWeight: 400 }}>
                        당일
                      </span>
                    </span>
                  ) : labels.col.change5m}
                </th>
              )}
            </tr>
          </thead>
          <tbody>
            {sorted.map((s, idx) => {
              const up   = s.change_rate > 0;
              const down = s.change_rate < 0;
              const color = up ? colors.up : down ? colors.down : colors.neutral;

              // 위장 모드 시 종목명/코드 추상화
              const displayName = disguised ? disguiseName(s.name, idx) : s.name;
              const displayCode = disguised ? disguiseCode(s.code, idx) : s.code;

              return (
                <tr
                  key={s.code}
                  onClick={() => onSelect(s.code, s.name)}
                  style={{
                    cursor: 'pointer',
                    background: s.code === selected ? '#1f2937' : 'transparent',
                    borderBottom: '1px solid #21262d',
                  }}
                >
                  {/* 종목명/Item */}
                  <td style={{ ...styles.td, ...(col.name === 'auto' ? {} : { width: col.name, maxWidth: col.name }), overflow: isMobile ? 'visible' : 'hidden' }}>
                    <div style={{
                      fontWeight: 600, fontSize: fs.name,
                      whiteSpace: isMobile ? 'normal' : 'nowrap',
                      overflow: isMobile ? 'visible' : 'hidden',
                      textOverflow: isMobile ? 'clip' : 'ellipsis',
                      overflowWrap: isMobile ? 'anywhere' : 'normal',
                      lineHeight: isMobile ? 1.3 : 1.4,
                    }}>
                      {displayName}
                    </div>
                    <div style={{ color: '#8b949e', fontSize: fs.code, whiteSpace: 'nowrap' }}>
                      {displayCode}
                    </div>
                  </td>
                  {/* 현재가/Score - 모바일 숨김 */}
                  {showPrice && (
                    <td style={{
                      ...styles.td, width: col.price, textAlign: 'right',
                      fontSize: fs.price, fontWeight: 600, whiteSpace: 'nowrap',
                      opacity: valueOpacity,
                    }}>
                      {s.price?.toLocaleString()}
                    </td>
                  )}
                  {/* 등락률/Variance — 패턴 모드에선 기간%(30봉) 병기.
                      라벨·색은 30봉 기준인데 이 숫자는 당일이라, 근거를 함께 보여줘야
                      '급등인데 왜 파란불?' 같은 오해가 생기지 않는다. */}
                  <td style={{ ...styles.td, width: col.changeRate }}>
                    <div style={{
                      textAlign: 'right', fontWeight: 700, fontSize: fs.rate,
                      whiteSpace: 'nowrap', color, opacity: valueOpacity,
                    }}>
                      {up ? '+' : ''}{s.change_rate?.toFixed(2)}%
                    </div>
                    {patternKey && s.period_pct != null && (
                      <div
                        title={`30거래일 기간 수익률 ${s.period_pct >= 0 ? '+' : ''}${s.period_pct.toFixed(1)}% — 형태 분류의 기준값입니다 (위 숫자는 당일 등락률)`}
                        style={{
                          textAlign: 'right', fontSize: Math.max(fs.rate - 3, 9),
                          whiteSpace: 'nowrap', marginTop: 1, opacity: valueOpacity,
                          color: s.period_pct >= 0 ? colors.up : colors.down,
                        }}
                      >
                        <span style={{ color: '#8b949e' }}>30일 </span>
                        {s.period_pct >= 0 ? '+' : ''}{s.period_pct.toFixed(1)}%
                      </div>
                    )}
                    {/* 모바일은 지표 컬럼(showChange5m)이 숨겨진다.
                        조건의 근거인 지표가 안 보이면 등락률 목록과 구분이 안 되므로
                        등락률 아래에 병기한다. 컬럼을 늘리지 않아 종목명 폭도 유지. */}
                    {isMobile && flowKey && s.flow_metric && (
                      <div
                        title={`${FLOW_COL_LABEL[flowKey] || '지표'} ${s.flow_metric.text}`}
                        style={{
                          textAlign: 'right', fontSize: Math.max(fs.rate - 3, 9),
                          whiteSpace: 'nowrap', marginTop: 1, opacity: valueOpacity,
                          color: s.flow_metric.tone === 'down' ? colors.down
                               : s.flow_metric.tone === 'flat' ? (excel ? '#000' : '#c9d1d9')
                               : colors.up,
                        }}
                      >
                        <span style={{ color: '#8b949e' }}>{FLOW_COL_LABEL_SHORT[flowKey] || ''} </span>
                        {s.flow_metric.text}
                      </div>
                    )}
                  </td>
                  {/* 거래금액/Volume - 모바일 숨김 */}
                  {showTradeValue && (
                    <td style={{
                      ...styles.td, width: col.tradeValue, textAlign: 'right',
                      fontSize: fs.value, color: '#8b949e', whiteSpace: 'nowrap',
                      opacity: valueOpacity,
                    }}>
                      {s.trade_value > 0
                        ? (disguised
                            ? `${Math.round(s.trade_value / 1000000)}`
                            : `${Math.round(s.trade_value / 1000000).toLocaleString()}`)
                        : '-'}
                    </td>
                  )}
                  {/* 5분대비/Δ - 모바일 숨김. 조건검색 모드는 미제공(-) */}
                  {showChange5m && (
                    <td style={{
                      ...styles.td, width: col.change5m, textAlign: 'center',
                      fontSize: fs.change5m, whiteSpace: 'nowrap', overflow: 'hidden',
                      opacity: valueOpacity,
                    }}>
                      {patternKey ? (
                        <Spark
                          points={s.spark?.points}
                          color={(s.period_pct ?? 0) >= 0 ? colors.up : colors.down}
                          width={(col.change5m || 60) - 20}
                        />
                      ) : flowKey ? (() => {
                        const med  = baseline[s.code];
                        const vol  = s.live_volume;
                        const live = (med && vol) ? vol / med : null;
                        const el   = marketElapsed();
                        const pace = (live !== null && el && el > 0.05) ? live / el : null;
                        const tip  = live === null
                          ? '장중 거래량 수신 대기'
                          : `현재 거래량 ${Number(vol).toLocaleString()} / 20일 중앙값 ${Number(med).toLocaleString()}`
                            + (pace ? `\n장 경과 ${Math.round(el * 100)}% · 이 페이스 유지 시 약 ${fmtRatio(pace)}` : '');
                        return (
                          <span style={{ display: 'inline-block', lineHeight: 1.15 }} title={tip}>
                            <span style={{
                              fontWeight: 700,
                              color: s.flow_metric?.tone === 'down' ? colors.down
                                   : s.flow_metric?.tone === 'flat' ? (excel ? '#000' : '#c9d1d9')
                                   : colors.up,
                            }}>{s.flow_metric?.text ?? '-'}</span>
                            <br />
                            <span style={{
                              fontSize: '0.82em',
                              color: live === null ? '#484f58'
                                   : live >= 1 ? '#d29922' : '#6e7681',
                              fontWeight: live !== null && live >= 1 ? 700 : 400,
                            }}>{fmtRatio(live) ?? '–'}</span>
                          </span>
                        );
                      })() : screenerKey ? (
                        <span style={{ color: '#555' }}>-</span>
                      ) : (
                        <>
                          {s.change_10m_type === 'NONE' && <span style={{ color: '#555' }}>-</span>}
                          {s.change_10m_type === 'NEW'  && (
                            <span style={{ color: disguised ? '#8b949e' : '#58a6ff', fontWeight: 700 }}>
                              {disguised ? 'NEW' : 'NEW'}
                            </span>
                          )}
                          {s.change_10m_type === 'HOLD' && s.change_rate >= 29.5
                            ? <span style={{ color: colors.up, fontWeight: 700 }}>
                                {disguised ? 'MAX' : '상한유지'}
                              </span>
                            : s.change_10m_type === 'HOLD'
                            ? <span style={{ color: '#8b949e' }}>→</span>
                            : null}
                          {s.change_10m_type === 'UP'   && (
                            <span style={{ color: colors.up, fontWeight: 700 }}>
                              ▲{s.change_10m?.toFixed(1)}
                            </span>
                          )}
                          {s.change_10m_type === 'DOWN' && (
                            <span style={{ color: colors.down, fontWeight: 700 }}>
                              ▼{Math.abs(s.change_10m)?.toFixed(1)}
                            </span>
                          )}
                        </>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
            {sorted.length === 0 && !loading && (
              <tr>
                <td colSpan={isMobile ? 2 : 5} style={{ textAlign: 'center', padding: 20, color: '#8b949e', fontSize: 12 }}>
                  {(patternKey && !disguised) ? '해당 모양의 종목이 없습니다'
                    : (flowKey && !disguised) ? '조건에 해당하는 종목이 없습니다'
                    : (screenerKey && !disguised) ? '조건에 해당하는 종목이 없습니다'
                    : labels.empty}
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const styles = {
  panel:     { height: '100%', display: 'flex', flexDirection: 'column' },
  header:    { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8, paddingBottom: 8, borderBottom: '1px solid #21262d' },
  label:     { fontWeight: 700, fontSize: 14, whiteSpace: 'nowrap' },
  filterRow: { display: 'flex', gap: 4, marginBottom: 6 },
  filterBtn: { fontSize: 12, padding: '3px 8px', borderRadius: 4, cursor: 'pointer', border: 'none' },
  tableWrap: { flex: 1, overflowY: 'auto', overflowX: 'hidden', paddingBottom: 8 },
  table:     { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed', minWidth: 0 },
  thead:     { color: '#8b949e', borderBottom: '1px solid #21262d', position: 'sticky', top: 0, background: '#0d1117' },
  th:        { padding: '4px 8px', textAlign: 'left', fontSize: 12, fontWeight: 500, whiteSpace: 'nowrap' },
  td:        { padding: '6px 8px' },
};
